import { ApolloClient, ApolloLink, execute, InMemoryCache, ServerError } from '@apollo/client';
import { omit } from 'lodash-es';
import type { Simplify } from 'type-fest';

import { trimStack, updateStack } from '@change-corgi/core/error';
import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import {
	GQLResponseError,
	GQLResponsePerimeterXChallengeError,
	GQLResponseServerError,
} from '@change-corgi/core/gqlErrors';
import { isPerimeterXEnforcerChallenge } from '@change-corgi/core/perimeterx';

import { errorHandlerLink } from './errorHandlerLink';
import { wrapFetch } from './fetch';
import type { HttpLinkOptions, HttpLinks } from './getHttpLinks';
import { getHttpLinks } from './getHttpLinks';
import { makePromise } from './makePromise';
import { operationIdLink } from './operationIdLink';
import { parseQuery } from './parser';
import { responseInfoLink } from './responseInfoLink';
import type { GQLFetchRequest } from './types';
import type { WebappInfo } from './webappInfoLink';
import { webappInfoLink } from './webappInfoLink';

export type Options = HttpLinkOptions & {
	errorReporter: ErrorReporter;
	networkErrorReporter: ErrorReporter;
	addOperationNamesToUri?: boolean;
	webappInfo?: WebappInfo;
	/**
	 * see wrapFetch
	 */
	offNetworkGatewayTimeoutSampleRate?: number;
};

type DecoratedFetchResult<
	// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
	TData = Record<string, any>,
	// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
	TExtensions = Record<string, any>,
> = ApolloLink.Result<TData, TExtensions> & {
	/**
	 * The requestId coming from the response headers. Can be useful for debugging.
	 */
	requestId?: string;
};

type FetchResultWithoutErrors<DATA extends Record<string, unknown> = Record<string, unknown>> = Omit<
	DecoratedFetchResult<DATA>,
	'errors' | 'data'
> & { data: DATA };

/**
 * Prevents TypeScript from inferring `GqlClient.fetch()`'s type parameters when none are passed
 * explicitly, forcing callers to specify them — using this hack since
 * https://www.typescriptlang.org/docs/handbook/utility-types.html#noinfertype does not work here.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
type BlockInfer = { __no_infer__: false };
type BlockInferWarning = { data: 'Please pass type parameters to GqlClient.fetch()' };
type BlockInferParamWarning = {
	query: 'Please pass type parameters to GqlClient.fetch()';
	variables: 'Please pass type parameters to GqlClient.fetch()';
};

export type GqlFetch = <
	DATA extends Record<string, unknown> = Record<string, unknown>,
	VARS extends Readonly<Record<string, unknown>> = Readonly<Record<string, unknown>>,
>(
	operation: GQLFetchRequest<VARS> & {
		rejectOnError?: true;
	},
) => Promise<FetchResultWithoutErrors<DATA>>;

/* eslint-disable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */
interface GqlClient {
	/**
	 * @throws {GQLResponseError} if there are errors in the response body
	 * @throws {GQLResponseServerError} if the response is a server error
	 * @throws {GQLResponsePerimeterXChallengeError} if the response is a PerimeterX challenge
	 */
	fetch<DATA extends Record<string, unknown> = BlockInfer, VARS extends Readonly<Record<string, unknown>> = BlockInfer>(
		operation: VARS extends BlockInfer
			? Simplify<BlockInferParamWarning>
			: GQLFetchRequest<NoInfer<VARS>> & {
					rejectOnError?: true;
				},
	): DATA extends BlockInfer
		? Promise<Simplify<BlockInferWarning>>
		: VARS extends BlockInfer
			? Promise<Simplify<BlockInferWarning>>
			: Promise<Simplify<FetchResultWithoutErrors<DATA>>>;
}
/* eslint-enable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */

class GqlClientImpl {
	private readonly options: Options;
	private readonly links: HttpLinks;
	private readonly _fetch: typeof fetch;
	private readonly webappInfoLink: ApolloLink;
	private readonly errorHandlerLink: ApolloLink;
	private readonly client: ApolloClient;

	constructor(options: Options) {
		const { errorReporter, networkErrorReporter, uri, headers, fetch: fetchOverride, addOperationNamesToUri } = options;
		this._fetch = wrapFetch({
			fetch: fetchOverride || fetch,
			errorReporter,
			networkErrorReporter,
			addOperationNamesToUri,
			webappInfo: options.webappInfo,
			offNetworkGatewayTimeoutSampleRate: options.offNetworkGatewayTimeoutSampleRate,
		});
		this.options = options;
		this.links = getHttpLinks({ uri, headers, fetch: this._fetch });
		this.errorHandlerLink = errorHandlerLink(errorReporter);
		this.webappInfoLink = webappInfoLink(options.webappInfo);
		this.client = new ApolloClient({
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'ignore' },
				query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
			},
			link: ApolloLink.empty(),
		});
	}

	private getUri(path: string | undefined) {
		const { uri } = this.options;
		return path ? `${uri}${path}` : uri;
	}

	async fetch<DATA extends Record<string, unknown> = BlockInfer>(
		operationP: unknown,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<any> {
		// this is used to get the stacktrace of the caller instead of ending up
		// with an unhelpful stacktrace pointing to "fetch" in some browsers
		const { stack } = new Error();
		const stackTrim = 1;

		const operation = operationP as GQLFetchRequest;

		if (operation.path && !operation.path.startsWith('/')) {
			throw new Error('[gql.fetch] custom path must start with a slash');
		}

		const query = parseQuery(operation.query);
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		const promise = makePromise(
			execute(
				/**
				 * operationIdLink => errorHandlerLink => responseInfoLink => httpLink
				 *                     (does nothing)      (does nothing)         |
				 *                                                                v
				 * operationIdLink <= errorHandlerLink <= responseInfoLink <= httpLink
				 *                  (needs response info) (adds response info
				 *                                           to extensions)
				 */
				ApolloLink.from([
					this.webappInfoLink,
					operationIdLink,
					this.errorHandlerLink,
					responseInfoLink,
					operation.batched === false ? this.links.single : this.links.batch,
				]),
				{
					...omit(operation, ['batched', 'path', 'rejectOnError']),
					context: {
						...operation.context,
						// this will override the uri set in getHttpLinks()
						uri: this.getUri(operation.path),
						stack,
						stackTrim,
					},
					query,
				},
				{ client: this.client },
			),
		) as Promise<DecoratedFetchResult<DATA>>;

		return handleGqlResponse<DATA>(promise, {
			stack,
			stackTrim,
		});
	}
}

export async function handleGqlResponse<DATA extends Record<string, unknown> = Record<string, unknown>>(
	promise: Promise<DecoratedFetchResult<DATA>>,
	options: { stack: string | undefined; stackTrim: number },
): Promise<FetchResultWithoutErrors<DATA>> {
	try {
		const result = await promise;
		const requestId = result.extensions?.requestId as string | undefined;
		if (result.errors) {
			// remove first line of stacktrace which would be the current function
			throw trimStack(
				updateStack(
					new GQLResponseError({
						errors: result.errors,
						requestId,
					}),
					options.stack,
				),
				options.stackTrim,
			);
		}
		if (!result.data) {
			// remove first line of stacktrace which would be the current function
			throw trimStack(
				updateStack(
					new GQLResponseError({
						message: 'No data in GQL response',
						errors: [],
						requestId,
					}),
					options.stack,
				),
				options.stackTrim,
			);
		}
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `result.data` was checked above
		return { ...result, requestId } as FetchResultWithoutErrors<DATA>;
	} catch (err) {
		throw trimStack(updateStack(serverErrorToGqlResponseError(err), options.stack), options.stackTrim);
	}
}

function parseBodyText(bodyText: string) {
	try {
		if (typeof bodyText === 'string') {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return JSON.parse(bodyText);
		}
		return bodyText;
	} catch {
		return undefined;
	}
}

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any */
function extractErrorsFromBody(bodyResult: any): any[] {
	if (!bodyResult || typeof bodyResult !== 'object') {
		return [];
	}
	// batched-response shape: an array of single GQL responses
	if (Array.isArray(bodyResult)) {
		return bodyResult.flatMap((result) =>
			result && typeof result === 'object' && Array.isArray(result.errors) ? result.errors : [],
		);
	}
	// single-response shape: { errors: [...], data: ... }
	if (Array.isArray(bodyResult.errors)) {
		return bodyResult.errors;
	}
	return [];
}

function serverErrorToGqlResponseError(err: any) {
	if (err.name !== 'ServerError' && !(err instanceof Error) && err.status) {
		const response = err as Response;
		// this appears to be a Response object that was not transformed into a ServerError
		try {
			throw new ServerError(response.statusText, { response, bodyText: response.body as unknown as string });
		} catch (serverError) {
			// eslint-disable-next-line no-param-reassign
			err = serverError;
		}
	}
	if (err.name === 'ServerError') {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		const serverError = err as ServerError;
		const bodyResult = parseBodyText(serverError.bodyText);
		if (serverError.statusCode === 403 && isPerimeterXEnforcerChallenge(bodyResult)) {
			return new GQLResponsePerimeterXChallengeError({ serverError, pxChallenge: bodyResult });
		}
		const errors = extractErrorsFromBody(bodyResult);
		return new GQLResponseServerError({
			errors,
			serverError,
			requestId: serverError.response.headers.get('x-request-id') || undefined,
		});
	}
	return err;
}
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any */

export type { GqlClient };

export function createGqlClient(options: Options): GqlClient {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see GqlClient's fetch() overload
	return new GqlClientImpl(options) as GqlClient;
}

export function createGqlClientFake(errorMsg: string): GqlClient {
	const errorFn = async () => {
		return Promise.reject(new Error(errorMsg));
	};
	// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see GqlClient's fetch() overload
	return {
		fetch: errorFn,
	} as GqlClient;
}
